import React, { FC, useRef, useEffect, useState, useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Card, Collapse, Divider, Empty, message, Skeleton, Typography } from 'antd';
import { RefSelectProps } from 'antd/lib/select';

import { Auth0User, Role, SelectItem } from 'types/global';
import { clientContractState, selectedClientState } from 'recoil/atoms';
import { SfAccountsSearch } from 'components/salesforce/SfAccountsSearch';
import './AccountAdmin.scss';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { useAdminApi } from '../../api/admin';
import { useAccountSettingsApi } from '../../api/accountSettings';
import { useClientApi } from '../../api/useClient';
import { AccountInfoDetails } from '../account-settings/AccountInfoDetails';
import { ClientUserManager } from 'components/user-management/ClientUserManager';
import { AccountData } from '../../models/AccountData';
import { ACLCheck } from '../../shared/Helpers';
import { DefaultRole, DefaultRolesRes } from 'sb/models/Role';

const { Panel } = Collapse;

const AccountAdmin: FC = () => {
  const searchRef = useRef<RefSelectProps>(null);
  const setSelectedClient = useSetRecoilState(selectedClientState);
  const selectedClient = useRecoilValue(selectedClientState);
  const setContract = useSetRecoilState(clientContractState);
  const [accountInfo, setAccountInfo] = useState<AccountData>();
  const [emailDomains, setEmailDomains] = useState<any[]>([]);
  const [users, setUsers] = useState<Auth0User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [activeUsers, setActiveUsers] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [accountPanelCollapseKeys, setAccountPanelCollapseKeys] = useState<string[]>(['account-info']);
  const [availableRoles, setAvailableRoles] = useState<DefaultRole[]>([]);
  const [savedRoles, setSavedRoles] = useState<DefaultRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [userRoles, setUserRoles] = useState<Role[]>([]);

  // API
  const { getCurrentAccount } = useAccountSettingsApi();
  const { getClientIdFromSalesforceId } = useClientApi();
  const {
    createAuth0Organization,
    fetchDefaultRoles,
    fetchSavedDefaultRoles,
    getSelectedClientUsers,
    selectClientEmailDomains,
    selectUserRoles
  } = useAdminApi();

  useEffect(() => {
    (async () => {
      if (selectedClient && selectedClient?.key) {
        setLoading(true);
        setSavedRoles([]);
        // Clear users immediately when switching accounts
        setUsers([]);
        setActiveUsers(0);
        setLoadingUsers(false);

        const salesforceAccountId = selectedClient.key.toString();
        const clientLookup = await getClientIdFromSalesforceId(salesforceAccountId);
        const clientId = clientLookup?.client_id;
        const account = clientId ? await getCurrentAccount(clientId, { allowMissingContract: true }) : undefined;
        const currentContract = account?.CurrentContract || account?.Current_Contract__r || undefined;
        if (!clientId) {
          message.error('ClientID is not available for the selected client account');
        } else if (account && currentContract && !account.auth0_organization_id) {
          message.warning('Account is not provisioned yet.');
        }
        setAccountInfo(
          account ?? (clientId ? ({ client_id: clientId, Id: salesforceAccountId } as AccountData) : undefined)
        );
        setContract(currentContract);

        const emailDomains = await selectClientEmailDomains(selectedClient?.key?.toString());
        setEmailDomains(emailDomains);
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.key]);

  const fetchSelectedClientUsers = useCallback(async () => {
    if (accountInfo?.client_id) {
      setLoadingUsers(true);
      const res = await getSelectedClientUsers(accountInfo?.client_id);
      // Handle both empty arrays and actual user lists
      if (Array.isArray(res) && res.length > 0) {
        setUsers(res);
        setActiveUsers(res?.filter((user: any) => !user.blocked).length);
      } else {
        setUsers([]);
        setActiveUsers(0);
      }
      setLoadingUsers(false);
    } else {
      setUsers([]);
      setActiveUsers(0);
      setLoadingUsers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo?.client_id]);

  const fetchSavedAndDefaultRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      const defaultRoles: DefaultRolesRes | undefined = await fetchDefaultRoles(accountInfo?.client_id);
      const savedRoles: DefaultRolesRes | undefined = await fetchSavedDefaultRoles(accountInfo?.client_id);
      defaultRoles?.data && setAvailableRoles(defaultRoles?.data);
      savedRoles?.data && setSavedRoles(savedRoles.data);
      const fetchedUserRoles = await selectUserRoles(selectedClient?.key?.toString());
      setUserRoles(fetchedUserRoles || []);
    } catch (e) {
      message.error('Problem retrieving default roles');
    } finally {
      setLoadingRoles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo?.client_id, selectedClient?.key]);

  // Fetch users and roles only when client_id changes - NOT when callbacks change
  useEffect(() => {
    if (!accountInfo?.client_id) return;

    (async () => {
      await fetchSelectedClientUsers();
      await fetchSavedAndDefaultRoles();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountInfo?.client_id]);

  const handleSfAccountSearchChange = async (val: SelectItem | undefined) => {
    if (val) {
      setSelectedClient(val);
    } else {
      setSelectedClient(undefined);
    }
  };

  useEffect(() => {
    // Set focus in SfAccountsSearch component
    searchRef.current?.focus();
  }, []);

  const onUsersLinkClick = () => {
    if (!accountPanelCollapseKeys.includes('user-mgmt')) {
      setAccountPanelCollapseKeys([...accountPanelCollapseKeys, 'user-mgmt']);
    }
  };

  const handleCollapseSectionClick = (key: string[]) => {
    setAccountPanelCollapseKeys(key);
  };

  const handleProvisionAccountClick = async () => {
    if (!accountInfo?.Id) return;

    try {
      const results = await createAuth0Organization(accountInfo?.Id + '', accountInfo?.Name);
      if (results) {
        setAccountInfo((prev) => (prev ? { ...prev, auth0_organization_id: results.id } : undefined));
        message.success('Client account provisioned!');
      }
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'An error occurred while provisioning the account.';
      message.error(errorMsg, 8);
    }
  };

  return (
    <div className="account-admin ml-4">
      <Typography.Title level={4}>Account Management</Typography.Title>
      <Divider />
      <Card>
        <Typography.Title level={4}>Search Account</Typography.Title>
        <SfAccountsSearch
          ref={searchRef}
          className="w-[380px]"
          onChange={handleSfAccountSearchChange}
          placeholder="Search by Client Name"
        />
        {!selectedClient && <Empty description="Please search for any account to view the details" />}
        {selectedClient && (
          <>
            <Divider />
            <Collapse
              bordered={false}
              defaultActiveKey={['account-info']}
              activeKey={accountPanelCollapseKeys}
              expandIcon={({ isActive }) =>
                isActive ? (
                  <MinusSquareOutlined className="text-blue-500" />
                ) : (
                  <PlusSquareOutlined className="text-blue-500" />
                )
              }
              onChange={(key) => {
                handleCollapseSectionClick(key as string[]);
              }}
            >
              <Panel header={selectedClient?.label} key="account-info">
                <Skeleton loading={loading} active>
                  <AccountInfoDetails
                    accountInfo={accountInfo}
                    emailDomains={emailDomains}
                    setEmailDomains={setEmailDomains}
                    activeUsers={activeUsers}
                    siteAdmin={true}
                    onUsersLinkClick={onUsersLinkClick}
                    updateEnabled={ACLCheck(['update:all_companies', 'create:all_companies'])}
                    onProvisionAccount={handleProvisionAccountClick}
                    savedRoles={savedRoles}
                    availableRoles={availableRoles}
                    fetchSavedAndDefaultRoles={fetchSavedAndDefaultRoles}
                    loadingRoles={loadingRoles}
                  />
                </Skeleton>
              </Panel>
              <Panel header="Associated Users" key="user-mgmt">
                {accountInfo?.client_id ? (
                  <ClientUserManager
                    siteAdmin={true}
                    users={users}
                    loadingUsers={loadingUsers}
                    fetchUsers={fetchSelectedClientUsers}
                    emailDomains={emailDomains}
                    userRoles={userRoles}
                    disableAddUser={!accountInfo?.auth0_organization_id}
                    isFederatedSignon={!!accountInfo?.auth0_connection_id}
                  />
                ) : (
                  <Empty
                    description="Cannot load users. This account needs to be provisioned first."
                    className="my-8"
                  />
                )}
              </Panel>
            </Collapse>
          </>
        )}
      </Card>
    </div>
  );
};

export default AccountAdmin;
